import { getConfigValue } from '@/api/config'
import { isDevFullMenu } from '@/utils/app-config'

/** 韶音租户公共配置 key（系统设置 → 配置管理） */
export const SHOKZ_VALIDATION_CONFIG_NAME = 'custom.validation.enabled'

export const SHOKZ_VALIDATION_MODE = 'shokz'

export const DEFAULT_SHOKZ_VALIDATION_MODE = 'false'

function unwrapConfigValue(value: unknown): unknown {
  let current = value
  for (let depth = 0; depth < 6; depth += 1) {
    if (current === null || current === undefined) {
      return current
    }
    if (typeof current === 'string') {
      const text = current.trim()
      if (!text) {
        return ''
      }
      try {
        current = JSON.parse(text) as unknown
        continue
      } catch {
        return text
      }
    }
    if (typeof current !== 'object') {
      return current
    }
    const record = current as Record<string, unknown>
    if (record.content != null) {
      current = record.content
      continue
    }
    if (record.currentValue != null) {
      current = record.currentValue
      continue
    }
    if (record.value != null) {
      current = record.value
      continue
    }
    if (record.configValue != null) {
      current = record.configValue
      continue
    }
    return current
  }
  return current
}

/**
 * 判断配置值是否表示韶音模式（值为 shokz，忽略大小写）。
 */
export function isShokzValidationValue(value: unknown): boolean {
  return String(unwrapConfigValue(value) ?? '').trim().toLowerCase() === SHOKZ_VALIDATION_MODE
}

/**
 * 归一化配置值为校验模式字符串（shokz / false 等）。
 */
export function normalizeShokzValidationMode(value?: unknown): string {
  const text = String(unwrapConfigValue(value) ?? value ?? '').trim().toLowerCase()
  return text || DEFAULT_SHOKZ_VALIDATION_MODE
}

let cachedEnabled: boolean | null = null
let pendingEnabled: Promise<boolean> | null = null

/**
 * 从配置管理读取韶音开关（/sys/config/getConfigValue）。
 */
export async function loadShokzValidationConfig(options: { force?: boolean } = {}): Promise<boolean> {
  if (isDevFullMenu()) {
    cachedEnabled = true
    return true
  }
  if (!options.force && cachedEnabled !== null) {
    return cachedEnabled
  }
  if (!options.force && pendingEnabled) {
    return pendingEnabled
  }

  pendingEnabled = getConfigValue(SHOKZ_VALIDATION_CONFIG_NAME)
    .then(raw => {
      cachedEnabled = isShokzValidationValue(raw)
      if (import.meta.env.DEV) {
        console.log('[CT_SHOKZ]', { config: SHOKZ_VALIDATION_CONFIG_NAME, raw, enabled: cachedEnabled })
      }
      return cachedEnabled
    })
    .catch(error => {
      cachedEnabled = false
      if (import.meta.env.DEV) {
        console.warn('[CT_SHOKZ] load failed', error)
      }
      return false
    })
    .finally(() => {
      pendingEnabled = null
    })

  return pendingEnabled
}

/**
 * 清除韶音配置内存缓存（登出/切租户时调用）。
 */
export function resetShokzValidationConfigCache(): void {
  cachedEnabled = null
  pendingEnabled = null
}
